import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Button, Table, Tabs, Tab } from 'react-bootstrap'
import Select from 'react-select'
import Swal from 'sweetalert2'
import Spinner from 'react-bootstrap/Spinner'
import { getRequestOptions } from '../helpers/Fetchwrapper'
import { formatDecimalQuantity, numberFormat } from '../helpers/GlobalHelper'

interface BomOption {
	label: string;
	value: number;
}

interface RateLine {
	idFormulationItems: number;
	itemName: string;
	categoryName: string;
	unitName: string;
	percentage?: number;
	quantity: number;
	rate: number;
	amount: number;
}

interface RateData {
	product: {
		idProducts: number;
		productName: string;
		productCode: string;
		packing: string;
		size: number;
		unitName: string;
	};
	packingBoms: { idPackingBoms: number; bomName: string }[];
	selectedPackingBomId: number;
	selectedBomName: string;
	effectiveDate: string;
	itemLines: RateLine[];
	packingLines: RateLine[];
	itemSubtotal: number;
	packingSubtotal: number;
	grandTotal: number;
}

interface ProductCostItem {
	idProductCostItems: number;
	idItems: number;
	itemName: string;
	categoryName: string;
	unitName: string;
	percentage?: number | string;
	quantity: number;
	rate: number;
	amount: number;
}

interface ProductCostOverhead {
	idProductCostOverheads: number;
	overheadName: string;
	amount: number;
}

interface ProductCostData {
	itemCost: number;
	overheadCost: number;
	vat: number;
	weightedLoss: number;
	totalCost: number;
	items: ProductCostItem[];
	overheads: ProductCostOverhead[];
}

interface PackingBomViewRateModalProps {
	show: boolean;
	handleClose: () => void;
	product: {
		idProducts: number;
		productName: string;
		packing?: string;
		size?: number | string;
		unitName?: string;
	} | null;
	defaultPackingBomId?: number | null;
}

const showMissingRateError = (resp: any) => {
	const missingNames = (resp.missingItems || []).map((item: any) => item.itemName).join('<br>')
	Swal.fire({
		icon: 'error',
		title: 'Average Rate Missing',
		html: missingNames
			? `Average rate not found for the following items as of today:<br><br>${missingNames}`
			: (resp.message || 'Average rate not found for one or more items.'),
	})
}

const PackingBomViewRateModal: React.FC<PackingBomViewRateModalProps> = ({
	show,
	handleClose,
	product,
	defaultPackingBomId = null,
}) => {
	const [activeTab, setActiveTab] = useState('product')
	const [loading, setLoading] = useState(false)
	const [rateData, setRateData] = useState<RateData | null>(null)
	const [bomOptions, setBomOptions] = useState<BomOption[]>([])
	const [selectedBom, setSelectedBom] = useState<BomOption | null>(null)

	const [productBomOptions, setProductBomOptions] = useState<BomOption[]>([])
	const [selectedProductBom, setSelectedProductBom] = useState<BomOption | null>(null)
	const [productCostLoading, setProductCostLoading] = useState(false)
	const [productCostData, setProductCostData] = useState<ProductCostData | null>(null)

	const fetchRates = useCallback(async (idProducts: number, idPackingBom?: number | null) => {
		setLoading(true)
		setRateData(null)
		try {
			let url = `${process.env.REACT_APP_API_URL}packing-boms/${idProducts}/rates`
			if (idPackingBom) {
				url += `?id_packing_bom=${idPackingBom}`
			}
			const response = await fetch(url, getRequestOptions())
			const resp = await response.json()

			if (!resp.success) {
				showMissingRateError(resp)
				return
			}

			const data: RateData = resp.data
			setRateData(data)

			const options: BomOption[] = (data.packingBoms || []).map((bom) => ({
				label: bom.bomName,
				value: bom.idPackingBoms,
			}))
			setBomOptions(options)

			const selected = options.find((option) => option.value === data.selectedPackingBomId) || options[0] || null
			setSelectedBom(selected)
		} catch (error) {
			console.log(error)
			Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load rate data.' })
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchProductCost = useCallback(async (idProducts: number, idProductBom: number) => {
		setProductCostLoading(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}products/${idProducts}?id_product_bom=${idProductBom}`,
				getRequestOptions()
			)
			const resp = await response.json()
			if (!resp.success) {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: resp.message || resp.errorMessage || 'Could not load product formulation.',
				})
				setProductCostData(null)
				return
			}

			setProductCostData({
				itemCost: parseFloat(resp.data?.itemCost) || 0,
				overheadCost: parseFloat(resp.data?.overheadCost) || 0,
				vat: parseFloat(resp.data?.vat) || 0,
				weightedLoss: parseFloat(resp.data?.weightedLoss) || 0,
				totalCost: parseFloat(resp.data?.totalCost) || 0,
				items: resp.items || [],
				overheads: resp.overheads || [],
			})
		} catch (error) {
			console.log(error)
			Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load product formulation.' })
			setProductCostData(null)
		} finally {
			setProductCostLoading(false)
		}
	}, [])

	const fetchProductBoms = useCallback(async (idProducts: number) => {
		setProductCostData(null)
		setSelectedProductBom(null)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}product-boms/${idProducts}`,
				getRequestOptions()
			)
			const resp = await response.json()
			const options: BomOption[] = (resp.data || []).map((bom: any) => ({
				label: bom.bom_name,
				value: bom.id_product_boms,
			}))
			setProductBomOptions(options)
			const first = options[0] || null
			setSelectedProductBom(first)
			if (first) {
				fetchProductCost(idProducts, first.value)
			}
		} catch (error) {
			console.log(error)
			setProductBomOptions([])
			Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load product BOMs.' })
		}
	}, [fetchProductCost])

	useEffect(() => {
		if (show && product?.idProducts) {
			setActiveTab('product')
			fetchRates(product.idProducts, defaultPackingBomId)
			fetchProductBoms(product.idProducts)
		}
		if (!show) {
			setRateData(null)
			setBomOptions([])
			setSelectedBom(null)
			setProductBomOptions([])
			setSelectedProductBom(null)
			setProductCostData(null)
			setActiveTab('product')
		}
	}, [show, product?.idProducts, defaultPackingBomId, fetchRates, fetchProductBoms])

	const handleBomChange = (option: BomOption | null) => {
		if (!option || !product?.idProducts) {
			return
		}
		setSelectedBom(option)
		fetchRates(product.idProducts, option.value)
	}

	const handleProductBomChange = (option: BomOption | null) => {
		if (!option || !product?.idProducts) {
			return
		}
		setSelectedProductBom(option)
		fetchProductCost(product.idProducts, option.value)
	}

	const displayProduct = rateData?.product || product

	return (
		<Modal show={show} onHide={handleClose} size="xl" centered scrollable>
			<Modal.Header closeButton>
				<Modal.Title>
					View Rate {displayProduct?.productName ? `- ${displayProduct.productName}` : ''}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{displayProduct && (
					<div className="mb-4">
						<div className="row">
							<div className="col-md-4">
								<strong>Packing:</strong> {displayProduct.packing || '-'}
							</div>
							<div className="col-md-4">
								<strong>Size:</strong> {displayProduct.size} {displayProduct.unitName || ''}
							</div>
							{rateData?.effectiveDate && (
								<div className="col-md-4">
									<strong>Rate as of:</strong> {rateData.effectiveDate}
								</div>
							)}
						</div>
					</div>
				)}

				<Tabs
					activeKey={activeTab}
					onSelect={(key) => setActiveTab(key || 'product')}
					className="mb-4"
				>
					<Tab eventKey="product" title="Product Formulation">
						<div className="d-flex justify-content-end mb-3">
							<div style={{ minWidth: '240px', maxWidth: '320px' }}>
								<Select
									value={selectedProductBom}
									onChange={(option) => handleProductBomChange(option as BomOption | null)}
									options={productBomOptions}
									placeholder="Select Product BOM"
									isDisabled={productCostLoading || productBomOptions.length === 0}
								/>
							</div>
						</div>

						{productCostLoading && (
							<div className="text-center py-5">
								<Spinner animation="border" role="status" />
							</div>
						)}

						{!productCostLoading && productBomOptions.length === 0 && (
							<div className="text-center text-muted py-5">No product BOM found for this product.</div>
						)}

						{!productCostLoading && selectedProductBom && productCostData && (
							<>
								<h5 className="mb-3">Items</h5>
								<Table bordered striped size="sm" responsive className="mb-4">
									<thead>
										<tr>
											<th>Item</th>
											<th>Category</th>
											<th className="text-end">%</th>
											<th className="text-end">Qty</th>
											<th>Unit</th>
											<th className="text-end">Rate</th>
											<th className="text-end">Amount</th>
										</tr>
									</thead>
									<tbody>
										{productCostData.items.length === 0 ? (
											<tr>
												<td colSpan={7} className="text-center text-muted">No product formulation items found.</td>
											</tr>
										) : (
											productCostData.items.map((line) => (
												<tr key={`product-item-${line.idProductCostItems || line.idItems}`}>
													<td>{line.itemName}</td>
													<td>{line.categoryName}</td>
													<td className="text-end">{formatDecimalQuantity(line.percentage, 4)}</td>
													<td className="text-end">{formatDecimalQuantity(line.quantity)}</td>
													<td>{line.unitName}</td>
													<td className="text-end">{numberFormat(line.rate)}</td>
													<td className="text-end">{numberFormat(line.amount)}</td>
												</tr>
											))
										)}
									</tbody>
									{productCostData.items.length > 0 && (
										<tfoot>
											<tr>
												<th colSpan={6} className="text-end">Item Cost</th>
												<th className="text-end">{numberFormat(productCostData.itemCost)}</th>
											</tr>
										</tfoot>
									)}
								</Table>

								<h5 className="mb-3">Overheads</h5>
								<Table bordered striped size="sm" responsive className="mb-4">
									<thead>
										<tr>
											<th>Overhead</th>
											<th className="text-end">Amount</th>
										</tr>
									</thead>
									<tbody>
										{productCostData.overheads.length === 0 ? (
											<tr>
												<td colSpan={2} className="text-center text-muted">No overheads found.</td>
											</tr>
										) : (
											productCostData.overheads.map((line) => (
												<tr key={`product-overhead-${line.idProductCostOverheads}`}>
													<td>{line.overheadName}</td>
													<td className="text-end">{numberFormat(line.amount)}</td>
												</tr>
											))
										)}
									</tbody>
									{productCostData.overheads.length > 0 && (
										<tfoot>
											<tr>
												<th className="text-end">Overhead Cost</th>
												<th className="text-end">{numberFormat(productCostData.overheadCost)}</th>
											</tr>
										</tfoot>
									)}
								</Table>

								<div className="text-end">
									<div className="mb-1">VAT: {numberFormat(productCostData.vat)}</div>
									<h5 className="mb-0">
										Total Cost: <span className="text-primary">{numberFormat(productCostData.totalCost)}</span>
									</h5>
								</div>
							</>
						)}
					</Tab>

					<Tab eventKey="blending" title="Blending Formulation">
						<div className="d-flex justify-content-end mb-3">
							<div style={{ minWidth: '240px', maxWidth: '320px' }}>
								<Select
									value={selectedBom}
									onChange={(option) => handleBomChange(option as BomOption | null)}
									options={bomOptions}
									placeholder="Select Packing BOM"
									isDisabled={loading || bomOptions.length === 0}
								/>
							</div>
						</div>

						{loading && (
							<div className="text-center py-5">
								<Spinner animation="border" role="status" />
							</div>
						)}

						{!loading && !rateData && (
							<div className="text-center text-muted py-5">No blending formulation found for this product.</div>
						)}

						{!loading && rateData && (
							<>
								<h5 className="mb-3">Item BOM (Blending)</h5>
								<Table bordered striped size="sm" responsive className="mb-4">
									<thead>
										<tr>
											<th>Item</th>
											<th>Category</th>
											<th>Unit</th>
											<th className="text-end">%</th>
											<th className="text-end">Qty</th>
											<th className="text-end">Rate</th>
											<th className="text-end">Amount</th>
										</tr>
									</thead>
									<tbody>
										{rateData.itemLines.length === 0 ? (
											<tr>
												<td colSpan={7} className="text-center text-muted">No item BOM formulations found.</td>
											</tr>
										) : (
											rateData.itemLines.map((line) => (
												<tr key={`item-${line.idFormulationItems}`}>
													<td>{line.itemName}</td>
													<td>{line.categoryName}</td>
													<td>{line.unitName}</td>
													<td className="text-end">{formatDecimalQuantity(line.percentage, 4)}</td>
													<td className="text-end">{formatDecimalQuantity(line.quantity)}</td>
													<td className="text-end">{numberFormat(line.rate)}</td>
													<td className="text-end">{numberFormat(line.amount)}</td>
												</tr>
											))
										)}
									</tbody>
									{rateData.itemLines.length > 0 && (
										<tfoot>
											<tr>
												<th colSpan={6} className="text-end">Subtotal</th>
												<th className="text-end">{numberFormat(rateData.itemSubtotal)}</th>
											</tr>
										</tfoot>
									)}
								</Table>

								<h5 className="mb-3">Packing BOM</h5>
								<Table bordered striped size="sm" responsive className="mb-4">
									<thead>
										<tr>
											<th>Item</th>
											<th>Category</th>
											<th>Unit</th>
											<th className="text-end">Qty</th>
											<th className="text-end">Rate</th>
											<th className="text-end">Amount</th>
										</tr>
									</thead>
									<tbody>
										{rateData.packingLines.length === 0 ? (
											<tr>
												<td colSpan={6} className="text-center text-muted">No packing BOM formulations found.</td>
											</tr>
										) : (
											rateData.packingLines.map((line) => (
												<tr key={`packing-${line.idFormulationItems}`}>
													<td>{line.itemName}</td>
													<td>{line.categoryName}</td>
													<td>{line.unitName}</td>
													<td className="text-end">{formatDecimalQuantity(line.quantity)}</td>
													<td className="text-end">{numberFormat(line.rate)}</td>
													<td className="text-end">{numberFormat(line.amount)}</td>
												</tr>
											))
										)}
									</tbody>
									{rateData.packingLines.length > 0 && (
										<tfoot>
											<tr>
												<th colSpan={5} className="text-end">Subtotal</th>
												<th className="text-end">{numberFormat(rateData.packingSubtotal)}</th>
											</tr>
										</tfoot>
									)}
								</Table>

								<div className="text-end">
									<h5 className="mb-0">
										Grand Total: <span className="text-primary">{numberFormat(rateData.grandTotal)}</span>
									</h5>
								</div>
							</>
						)}
					</Tab>
				</Tabs>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="sm" onClick={handleClose}>
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default PackingBomViewRateModal
